import styled from 'styled-components';

import { Color, colors, fontWeights } from 'theme';

import { ButtonVariant } from './Button.types';
import {
  buttonAppearance,
  buttonSize,
  buttonStylesMinimal,
  linkAppearance,
} from './buttonStyles';

// name of colors for icon initial color
export const TEXT_COLORS: Record<ButtonVariant, Color> = {
  primary: 'white',
  secondary: 'brandBlue',
  'secondary-white': 'white',
  danger: 'white',
  'danger-outline': 'alertRedHover',
  'legacy-tertiary': 'mediumContentGrey',
  'legacy-tertiary-blue': 'brandBlue',
  'legacy-tertiary-white': 'white',
  tertiary: 'brandBlue',
  'tertiary-grey': 'mediumContentGrey',
  'tertiary-danger': 'alertRedHover',
  link: 'brandBlue',
  'link-white': 'white',
  inactive: 'mediumContentGrey',
};

export const StyledButton = styled.button`
  ${buttonStylesMinimal};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px solid transparent;

  &.full-width {
    width: 100%;
  }

  /* button variants: */
  &.primary {
    ${buttonAppearance};
    background-color: ${colors.brandBlue};
    border-color: ${colors.brandBlue};
    color: ${colors[TEXT_COLORS.primary]};
    &:hover,
    &:focus {
      background-color: ${colors.hoverBlue};
      border-color: ${colors.hoverBlue};
      text-decoration: none;
    }
  }
  &.secondary {
    ${buttonAppearance};
    border-color: ${colors.brandBlue};
    color: ${colors[TEXT_COLORS.secondary]};
    &:hover,
    &:focus {
      background-color: ${colors.softBlue};
      text-decoration: none;
    }
  }
  &.secondary-white {
    ${buttonAppearance};
    border-color: ${colors.white};
    color: ${TEXT_COLORS['secondary-white']};
    &:hover,
    &:focus {
      color: ${colors.brandBlue};
      background-color: ${colors.softBlue};
      text-decoration: none;
    }
  }
  &.danger {
    ${buttonAppearance};
    background-color: ${colors.alertRed};
    border-color: ${colors.alertRed};
    color: ${colors[TEXT_COLORS.danger]};
    &:hover,
    &:focus {
      background-color: ${colors.alertRedHover};
      border-color: ${colors.alertRedHover};
      text-decoration: none;
    }
  }
  &.danger-outline {
    ${buttonAppearance};
    border-color: ${colors.alertRed};
    color: ${colors[TEXT_COLORS['danger-outline']]};
    &:hover,
    &:focus {
      background-color: ${colors.alertRedHover};
      color: white;
      text-decoration: none;
    }
  }
  &.legacy-tertiary {
    ${buttonAppearance};
    color: ${colors[TEXT_COLORS['legacy-tertiary']]};

    &:hover,
    &:focus {
      border-color: ${colors.mediumContentGrey};
      text-decoration: none;
    }
  }
  &.legacy-tertiary-blue {
    ${buttonAppearance};
    color: ${TEXT_COLORS['legacy-tertiary-blue']};

    &:hover,
    &:focus {
      text-decoration: none;
    }
  }
  &.legacy-tertiary-white {
    ${buttonAppearance};
    color: ${TEXT_COLORS['legacy-tertiary-white']};

    &:hover,
    &:focus {
      text-decoration: none;
    }
  }
  &.tertiary {
    ${buttonAppearance};
    color: ${colors[TEXT_COLORS.tertiary]};
    font-weight: ${fontWeights.bold};
    &:hover,
    &:focus {
      color: ${colors.hoverBlue};
      text-decoration-line: underline;
      text-decoration-color: ${colors.hoverBlue};
    }
  }
  &.tertiary-grey {
    ${buttonAppearance};
    color: ${colors[TEXT_COLORS['tertiary-grey']]};
    font-weight: ${fontWeights.bold};
    &:hover,
    &:focus {
      color: ${colors.hoverBlue};
      text-decoration-line: underline;
      text-decoration-color: ${colors[TEXT_COLORS['tertiary-grey']]};
    }
  }
  &.tertiary-danger {
    ${buttonAppearance};
    color: ${colors[TEXT_COLORS['tertiary-danger']]};
    font-weight: ${fontWeights.bold};
    &:hover,
    &:focus {
      text-decoration-line: underline;
      text-decoration-color: ${colors[TEXT_COLORS['tertiary-danger']]};
    }
  }
  &.link {
    /* block-no-empty */
    ${linkAppearance}
  }
  &.link-white {
    ${linkAppearance};
    color: white;
    text-decoration-line: underline;

    &:hover {
      color: white;
      text-decoration-color: white;
    }
  }
  &.inactive {
    ${buttonAppearance};
    background-color: ${colors.selectionGrey};
    color: ${colors[TEXT_COLORS.inactive]};
  }
  /* button sizes: */
  &.tiny {
    /* block-no-empty */
    ${buttonSize({ size: 'tiny' })};
  }
  &.small {
    /* block-no-empty */
    ${buttonSize({ size: 'small' })};
  }
  &.medium {
    /* block-no-empty */
    ${buttonSize({ size: 'medium' })};
  }
  &.large {
    /* block-no-empty */
    ${buttonSize({ size: 'large' })};
  }
  &.small,
  &.medium,
  &.large {
    &.no-padding {
      padding: 0;
    }
  }
`;

export const ChildrenContainer = styled.div`
  text-align: center;
  width: 100%;
`;

// icons have a negative margin to compensate for padding internal to the SVG
export const IconContainer = styled.div`
  margin: -1px -2px;
`;
